import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import CountUp from "react-countup";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import {
  Flame, Trophy, Zap, ArrowRight, Clock, Award, TrendingUp, Star, Timer, Workflow, Swords,
  Target, GraduationCap, Terminal, Mic, Briefcase, Snowflake, Plus, Sparkles, Compass, CheckCircle2, LifeBuoy, FileSearch,
  BadgeCheck, MessagesSquare, Network, GitBranch, Braces, Mail, Clapperboard, ShoppingBag, Video,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AIInsightsCard from "@/components/AIInsightsCard";
import NexusCore from "@/components/nexus/NexusCore";
import RecruiterInsightsCard from "@/components/RecruiterInsightsCard";
import ReferralCard from "@/components/ReferralCard";
import HelpSupportModal from "@/components/HelpSupportModal";
import DailyMissionsWidget from "@/components/DailyMissionsWidget";
import TrackChooserSection from "@/components/TrackChooserSection";
import MyCustomChallengesWidget from "@/components/MyCustomChallengesWidget";
import { fireConfetti } from "@/lib/confetti";

const ACH_ICONS = { footprints: Award, flame: Flame, target: Target, "trending-up": TrendingUp, zap: Zap, award: Award, trophy: Trophy, medal: Trophy, database: Award, fingerprint: Award, calculator: Award, filter: Award, terminal: Award, workflow: Workflow, boxes: Award, swords: Swords };

const DIFFICULTY_STYLE = {
  Beginner: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  Intermediate: "bg-amber-400/10 text-amber-300 border-amber-400/20",
  Advanced: "bg-rose-400/10 text-rose-300 border-rose-400/20",
};

const container = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };
const item = { hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };
const cardHover = "transition-all duration-300 ease-out hover:-translate-y-1 hover:border-white/20 hover:shadow-[0_12px_40px_rgba(0,0,0,0.35)]";
const MotionLink = motion(Link);

function greetingForHour() {
  const h = new Date().getHours();
  if (h < 5) return "Burning the midnight oil";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Working late";
}

function ProgressRing({ value = 0, size = 64, stroke = 5, color = "#6366F1" }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, value)) / 100) * circumference;
  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
      <motion.circle
        cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circumference} strokeLinecap="round"
        initial={{ strokeDashoffset: circumference }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 1.1, ease: "easeOut", delay: 0.15 }}
      />
    </svg>
  );
}

function StatTile({ icon: Icon, color, label, value, suffix = "", testId, subNote }) {
  return (
    <motion.div
      className="neu-card rounded-[18px] p-4 sm:p-5 cursor-default"
      data-testid={testId}
      whileHover={{ y: -3, boxShadow: `8px 8px 22px rgba(1,4,12,0.65), -4px -4px 16px rgba(255,255,255,0.025), 0 0 0 1px ${color}40, 0 8px 24px ${color}22` }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ background: `${color}1a` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="font-mono text-xl sm:text-2xl font-bold text-[#eef1f8] tabular-nums">
        <CountUp end={value ?? 0} duration={1.3} separator="," />{suffix}
      </p>
      <p className="text-[11px] text-[#8993ab] mt-1">{label}</p>
      {subNote && <p className="text-[10px] text-[#5c6785] mt-1">{subNote}</p>}
    </motion.div>
  );
}

function NavCard({ to, testId, icon: Icon, color, title, desc, badge }) {
  return (
    <MotionLink
      to={to} data-testid={testId}
      className="neu-card rounded-2xl p-5 flex items-center gap-3 group relative"
      whileHover={{ y: -2, boxShadow: `8px 8px 22px rgba(1,4,12,0.65), -4px -4px 16px rgba(255,255,255,0.025), 0 0 0 1px ${color}40` }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      {badge && (
        <span className="absolute -top-2 -right-2 text-[9px] font-bold uppercase tracking-wide bg-gradient-to-r from-amber-400 to-orange-500 text-white px-2 py-0.5 rounded-full shadow-lg" data-testid={`${testId}-badge`}>
          {badge}
        </span>
      )}
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1a` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-white">{title}</p>
        <p className="text-[11px] text-slate-400">{desc}</p>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
    </MotionLink>
  );
}

function ToolTile({ to, testId, icon: Icon, color, title, badge }) {
  return (
    <MotionLink
      to={to} data-testid={testId}
      className="relative flex flex-col items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4 pb-11 group overflow-hidden min-h-[104px]"
      whileHover={{ y: -3, borderColor: `${color}55`, boxShadow: `0 10px 30px ${color}22` }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      {badge && (
        <span className="absolute top-3 right-3 text-[8px] font-bold uppercase tracking-wide bg-gradient-to-r from-amber-400 to-orange-500 text-white px-1.5 py-0.5 rounded-full">
          {badge}
        </span>
      )}
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1a` }}>
        <Icon className="w-4.5 h-4.5" style={{ color }} />
      </div>
      <p className="text-sm font-medium text-white leading-snug">{title}</p>
      <ArrowRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-white group-hover:translate-x-0.5 transition-all absolute bottom-4 right-4" />
    </MotionLink>
  );
}

const TOOL_TILES = [
  { to: "/resume-builder", testId: "dashboard-resume-checker-card", icon: FileSearch, color: "#38bdf8", title: "AI Resume Builder", badge: "New" },
  { to: "/meetings", testId: "dashboard-meet-card", icon: Video, color: "#22d3ee", title: "Cloudivo Meet", badge: "New" },
  { to: "/courses", testId: "dashboard-courses-card", icon: ShoppingBag, color: "#f472b6", title: "Browse Courses" },
  { to: "/creator/studio", testId: "dashboard-creator-studio-card", icon: Clapperboard, color: "#fb7185", title: "Creator Studio", badge: "New" },
  { to: "/duel", testId: "dashboard-duel-card", icon: Swords, color: "#fb7185", title: "Duel Mode" },
  { to: "/case-studies", testId: "dashboard-case-studies-card", icon: Briefcase, color: "#c084fc", title: "Case Studies" },
  { to: "/career", testId: "dashboard-career-card", icon: GraduationCap, color: "#34d399", title: "Career Center" },
  { to: "/interview", testId: "dashboard-interview-card", icon: Mic, color: "#FB923C", title: "Interview Center" },
  { to: "/certifications", testId: "dashboard-certifications-card", icon: BadgeCheck, color: "#a78bfa", title: "Certification Prep" },
  { to: "/community", testId: "dashboard-community-card", icon: MessagesSquare, color: "#fb923c", title: "Community" },
  { to: "/sql-playground", testId: "dashboard-sql-card", icon: Terminal, color: "#fbbf24", title: "SQL Playground" },
  { to: "/ampscript-playground", testId: "dashboard-ampscript-card", icon: Braces, color: "#2dd4bf", title: "AMPscript Playground" },
  { to: "/sfmc-flow-builder", testId: "dashboard-sfmc-flow-builder-card", icon: GitBranch, color: "#2dd4bf", title: "SFMC Flow Builder" },
  { to: "/email-template-compiler", testId: "dashboard-email-compiler-card", icon: Mail, color: "#2dd4bf", title: "Email-HTML Compiler" },
  { to: "/architecture", testId: "dashboard-architecture-card", icon: Network, color: "#22d3ee", title: "Architecture Visualizer" },
  { to: "/flow-builder", testId: "dashboard-flow-builder-card", icon: GitBranch, color: "#818cf8", title: "Visual Flow Builder" },
];

function DashboardSkeleton() {
  const Block = ({ className }) => <div className={`rounded-2xl bg-white/5 ${className}`} />;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 animate-pulse" data-testid="dashboard-loading">
      <Block className="h-44 mb-6" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {[0, 1, 2, 3, 4, 5].map((i) => <Block key={i} className="h-24" />)}
      </div>
      <Block className="h-16 mb-6" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Block className="h-40" />
          <Block className="h-56" />
          <Block className="h-40" />
        </div>
        <div className="space-y-6">
          <Block className="h-40" />
          <Block className="h-40" />
          <Block className="h-40" />
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyingFreeze, setBuyingFreeze] = useState(false);
  const [careerReport, setCareerReport] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    api.get("/users/me/dashboard").then((res) => {
      setData(res.data);
      const levelKey = `dc30_level_${user?.user_id}`;
      const prevLevel = parseInt(sessionStorage.getItem(levelKey) || "0", 10);
      if (prevLevel && res.data.level_info.level > prevLevel) {
        fireConfetti();
        toast.success(`Level Up! You're now Level ${res.data.level_info.level} · ${res.data.level_info.title}`);
      }
      sessionStorage.setItem(levelKey, res.data.level_info.level);
    }).finally(() => setLoading(false));
    api.get("/career/report").then((res) => setCareerReport(res.data)).catch(() => {});
  }, [user?.user_id]);

  const buyStreakFreeze = async () => {
    setBuyingFreeze(true);
    try {
      const { data: res } = await api.post("/users/me/streak-freeze/buy");
      setData((prev) => ({ ...prev, coins: res.coins, streak_freezes: res.streak_freezes }));
      toast.success(res.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not purchase Streak Freeze");
    } finally {
      setBuyingFreeze(false);
    }
  };

  if (loading || !data) {
    return <DashboardSkeleton />;
  }

  const chartData = data.weekly_progress.map((d) => ({
    day: new Date(d.date).toLocaleDateString("en-US", { weekday: "short" }),
    completed: d.completed,
  }));

  return (
    <>
      <motion.div initial="hidden" animate="visible" variants={container} className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-32" data-testid="dashboard-page">
      {/* Welcome Banner */}
      <motion.div variants={item} className="bg-gradient-to-br from-[#1a2333] to-[#131a29] border border-[#232d42] rounded-[24px] p-7 sm:p-10 mb-4 relative overflow-hidden">
        <div className="absolute -top-28 -right-16 w-72 h-72 bg-[#5b9dff]/[0.07] rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute -top-10 -left-10 w-64 h-64 bg-[#b58bff]/10 rounded-full blur-[80px] pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ffb020] flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> {greetingForHour()}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#eef1f8] mt-1.5" data-testid="dashboard-welcome-name">{data.name}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="text-sm font-mono text-[#8993ab]" data-testid="dashboard-current-day">Day {data.current_day} of {data.total_days}</span>
              <span className="text-[#5c6785]">·</span>
              <span className="inline-flex items-center gap-1 bg-[#b58bff]/10 border border-[#b58bff]/25 rounded-full px-2.5 py-0.5 text-[11px] font-bold text-[#b58bff]" data-testid="dashboard-level-badge">
                <Star className="w-3 h-3" /> Level {data.level_info.level} · {data.level_info.title}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-5 sm:gap-6">
            <div className="text-center" data-testid="dashboard-xp-stat">
              <div className="flex items-center gap-1 justify-center text-[#ffb020]"><Zap className="w-4 h-4" /><span className="font-mono text-2xl font-bold tabular-nums"><CountUp end={data.xp} duration={1.3} separator="," /></span></div>
              <p className="text-[11px] uppercase tracking-wider text-[#8993ab] mt-0.5">XP</p>
            </div>
            <div className="text-center" data-testid="dashboard-streak-stat">
              <div className="flex items-center gap-1 justify-center text-[#ff8a3d]"><Flame className="w-4 h-4" /><span className="font-mono text-2xl font-bold tabular-nums"><CountUp end={data.streak_current} duration={1.3} /></span></div>
              <p className="text-[11px] uppercase tracking-wider text-[#8993ab] mt-0.5">Streak</p>
            </div>
            <div className="text-center" data-testid="dashboard-rank-stat">
              <div className="flex items-center gap-1 justify-center text-[#5b9dff]"><Trophy className="w-4 h-4" /><span className="font-mono text-2xl font-bold tabular-nums">#<CountUp end={data.rank} duration={1.3} /></span></div>
              <p className="text-[11px] uppercase tracking-wider text-[#8993ab] mt-0.5">Rank</p>
            </div>
          </div>
        </div>

        <div className="relative mt-7">
          <div className="flex justify-between text-xs text-[#8993ab] mb-1.5">
            <span>Overall progress ({data.completed_count}/{data.total_days} days)</span>
            <span className="font-mono" data-testid="dashboard-progress-pct">{data.progress_pct}%</span>
          </div>
          <div className="w-full h-[10px] rounded-full bg-[#1b2434] neu-inset overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#5b9dff] via-[#8bb8ff] to-[#b58bff] animate-shimmer"
              style={{ width: `${Math.max(data.progress_pct, 3)}%`, backgroundImage: "linear-gradient(90deg,#5b9dff,#b58bff,#5b9dff)" }}
            />
          </div>
          <div className="flex justify-between text-[11px] text-[#8993ab] mt-3">
            <span>Level {data.level_info.level} progress</span>
            <span className="font-mono">{data.level_info.next_level_title ? `${data.level_info.progress_pct}% to ${data.level_info.next_level_title}` : "Max Level!"}</span>
          </div>
          <div className="w-full h-[10px] rounded-full bg-[#1b2434] neu-inset overflow-hidden mt-1.5">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#ffb020] to-[#ff8a3d] animate-shimmer"
              style={{ width: `${Math.max(data.level_info.progress_pct, 3)}%`, backgroundImage: "linear-gradient(90deg,#ffb020,#ff8a3d,#ffb020)" }}
            />
          </div>
        </div>

        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 pt-5 border-t border-[#232d42]">
          <div className="flex items-center gap-2" data-testid="streak-freeze-widget">
            <span className="flex items-center gap-1 text-xs font-mono text-[#eef1f8] bg-[#1b2434] rounded-full px-2.5 py-1.5">
              <Snowflake className="w-3.5 h-3.5 text-[#5b9dff]" /> {data.streak_freezes}/{data.streak_freeze_max} Streak Freezes
            </span>
            {data.streak_freezes < data.streak_freeze_max && (
              <motion.button
                onClick={buyStreakFreeze} disabled={buyingFreeze} data-testid="buy-streak-freeze-btn"
                whileTap={{ scale: 0.94 }}
                className="flex items-center gap-1 text-xs font-semibold text-[#eef1f8] bg-[#1b2434] hover:bg-[#232d42] rounded-full px-2.5 py-1.5 transition-colors disabled:opacity-50"
                title="Costs 50 coins — protects your streak if you miss a day"
              >
                <Plus className="w-3 h-3" /> Buy (50 coins)
              </motion.button>
            )}
          </div>
          {data.today_challenge && (
            <MotionLink
              to={`/challenge/${data.today_challenge.day}`}
              data-testid="dashboard-start-day-button"
              whileHover={{ y: -2, boxShadow: "0 12px 32px rgba(91,157,255,0.35)" }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-[#5b9dff] to-[#b58bff] px-5 py-2.5 rounded-[10px] shadow-lg shadow-[#5b9dff]/20"
            >
              {data.completed_count > 0 ? "Continue Learning" : `Start Day ${data.today_challenge.day} →`}
            </MotionLink>
          )}
        </div>
      </motion.div>

      {/* Today's Task Banner — always give a new/zero-state user a concrete next action */}
      {data.today_challenge && (
        <motion.div
          variants={item}
          data-testid="dashboard-todays-task-banner"
          className="bg-[#ffb020]/10 border border-[#ffb020]/20 rounded-[14px] p-4 flex flex-col sm:flex-row justify-between items-center gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#ffb020]/15 flex items-center justify-center shrink-0">
              <Target className="w-5 h-5 text-[#ffb020]" />
            </div>
            <div>
              <p className="font-heading text-sm font-semibold text-[#eef1f8]">
                Today's task — {data.today_challenge.status === "completed" ? "Review" : "Complete"} Day {data.today_challenge.day}: {data.today_challenge.title}
              </p>
              <p className="text-xs text-[#8993ab] mt-0.5">
                ~{data.today_challenge.estimated_minutes} min · <span className="font-mono">+{data.today_challenge.xp_reward} XP</span>
              </p>
            </div>
          </div>
          <MotionLink
            to={`/challenge/${data.today_challenge.day}`}
            data-testid="dashboard-todays-task-begin-button"
            whileTap={{ scale: 0.94 }}
            whileHover={{ y: -1 }}
            className="inline-flex items-center justify-center gap-1.5 shrink-0 bg-[#ffb020] hover:bg-[#e6a01c] text-[#0a0d16] font-bold px-5 py-2 rounded-[10px] transition-colors"
          >
            Begin <ArrowRight className="w-3.5 h-3.5" />
          </MotionLink>
        </motion.div>
      )}

      <TrackChooserSection
        dataCloudStats={{ completed_count: data.completed_count, total_days: data.total_days, streak_current: data.streak_current }}
      />

      {/* Statistics Section */}
      <motion.div variants={item} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatTile icon={Zap} color="#ffb020" label="Total XP" value={data.xp} testId="dashboard-stat-xp" />
        <StatTile icon={Star} color="#b58bff" label={`Level · ${data.level_info.title}`} value={data.level_info.level} testId="dashboard-stat-level" />
        <StatTile icon={CheckCircle2} color="#3ddc84" label="Challenges Completed" value={data.completed_count} suffix={`/${data.total_days}`} testId="dashboard-stat-completed" subNote={data.completed_count === 0 ? "Start Day 1 to begin" : undefined} />
        <StatTile icon={Flame} color="#ff8a3d" label="Current Streak" value={data.streak_current} suffix=" days" testId="dashboard-stat-streak" subNote={data.streak_current === 0 ? "Complete today to start one" : undefined} />
        <StatTile icon={Target} color="#5b9dff" label="AI Career Score" value={careerReport?.job_readiness_score ?? 0} suffix="%" testId="dashboard-stat-career-score" />
        <StatTile icon={Compass} color="#2fe0c0" label="Job Readiness" value={careerReport?.career_progress_pct ?? 0} suffix="%" testId="dashboard-stat-job-readiness" />
      </motion.div>

      {/* Simulator Banner */}
      <motion.div variants={item}>
        <MotionLink
          to="/simulator" data-testid="dashboard-simulator-banner"
          whileHover={{ y: -2, boxShadow: "0 12px 32px rgba(168,85,247,0.25)" }}
          whileTap={{ scale: 0.98 }}
          className="neu-card rounded-2xl p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-indigo-500/5 to-transparent" />
          <div className="relative w-12 h-12 rounded-xl bg-purple-500/15 flex items-center justify-center shrink-0">
            <Workflow className="w-6 h-6 text-purple-300" />
          </div>
          <div className="relative flex-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-300">New · Signature Feature</span>
            <h3 className="font-heading font-semibold text-white mt-0.5">Interactive Data Cloud Simulator</h3>
            <p className="text-xs text-slate-400 mt-0.5">Learn by doing — connect sources, map fields, resolve identities & activate audiences across 4 real business scenarios.</p>
          </div>
          <span className="relative inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-primary group-hover:bg-primary/90 px-4 py-2 rounded-lg transition-colors shrink-0">
            Try It Now <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </MotionLink>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {data.today_challenge && (
            <motion.div variants={item} className="neu-card rounded-2xl p-6 flex items-start gap-5" data-testid="dashboard-today-challenge" whileHover={{ boxShadow: "8px 8px 22px rgba(1,4,12,0.65), -4px -4px 16px rgba(255,255,255,0.025), 0 0 0 1px rgba(99,102,241,0.4)" }}>
              <ProgressRing value={data.today_challenge.status === "completed" ? 100 : data.progress_pct} color={data.today_challenge.status === "completed" ? "#34d399" : "#6366F1"} />
              <div className="flex-1">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Today's Challenge</span>
                <h2 className="font-heading text-xl font-semibold text-white mt-2">Day {data.today_challenge.day}: {data.today_challenge.title}</h2>
                <div className="flex items-center gap-3 mt-3 text-xs text-slate-400 flex-wrap">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {data.today_challenge.estimated_minutes} min</span>
                  <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> {data.today_challenge.xp_reward} XP</span>
                  <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${DIFFICULTY_STYLE[data.today_challenge.difficulty] || "bg-white/5 border-white/10 text-slate-300"}`}>{data.today_challenge.difficulty}</span>
                </div>
                <MotionLink
                  to={`/challenge/${data.today_challenge.day}`} data-testid="dashboard-continue-challenge-btn"
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ y: -2, boxShadow: "0 12px 28px rgba(99,102,241,0.35)" }}
                  className="mt-5 inline-flex items-center gap-2 bg-primary text-white font-semibold px-5 py-2.5 rounded-lg"
                >
                  {data.today_challenge.status === "completed" ? "Review Challenge" : "Continue Challenge"} <ArrowRight className="w-4 h-4" />
                </MotionLink>
              </div>
            </motion.div>
          )}

          <motion.div variants={item} className="grid grid-cols-2 gap-4">
            <div className="neu-card rounded-2xl p-5" data-testid="dashboard-weekly-time-stat">
              <Timer className="w-4 h-4 text-indigo-400 mb-2" />
              <p className="font-heading text-2xl font-bold text-white tabular-nums"><CountUp end={data.weekly_learning_minutes} duration={1.2} /><span className="text-sm text-slate-400 ml-1">min</span></p>
              <p className="text-xs text-slate-400 mt-0.5">This Week's Learning Time</p>
            </div>
            <div className="neu-card rounded-2xl p-5" data-testid="dashboard-completed-stat">
              <Award className="w-4 h-4 text-emerald-400 mb-2" />
              <p className="font-heading text-2xl font-bold text-white tabular-nums"><CountUp end={data.completed_count} duration={1.2} /><span className="text-sm text-slate-400 ml-1">/ {data.total_days}</span></p>
              <p className="text-xs text-slate-400 mt-0.5">Challenges Completed</p>
            </div>
          </motion.div>

          <motion.div variants={item} className="neu-card rounded-2xl p-6" data-testid="dashboard-weekly-chart">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5" /> Weekly Progress</span>
            <div className="h-40 min-h-[160px] mt-4">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                <BarChart data={chartData}>
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={1} />
                      <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="day" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={{ background: "rgba(5,17,36,0.95)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 12 }} labelStyle={{ color: "#94a3b8" }} />
                  <Bar dataKey="completed" fill="url(#barGradient)" radius={[6, 6, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
          <motion.div variants={item}><AIInsightsCard /></motion.div>
          <motion.div variants={item}><NexusCore /></motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <motion.div variants={item}><RecruiterInsightsCard data={data.recruiter_insights} /></motion.div>
          <motion.div variants={item}><DailyMissionsWidget /></motion.div>
          <motion.div variants={item}><ReferralCard /></motion.div>
          <motion.div variants={item}><MyCustomChallengesWidget /></motion.div>

          <motion.div variants={item} className="neu-card rounded-2xl p-6" data-testid="dashboard-leaderboard-widget">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Top Learners</span>
              <Link to="/leaderboard" className="text-xs text-indigo-400 hover:text-indigo-300">View all</Link>
            </div>
            <div className="space-y-3">
              {data.leaderboard_top5.map((u) => {
                const medal = { 1: "#FBBF24", 2: "#CBD5E1", 3: "#F59E0B" }[u.rank];
                return (
                  <div key={u.user_id} className="flex items-center gap-3" data-testid={`leaderboard-widget-row-${u.rank}`}>
                    <span className="w-5 text-xs font-bold" style={{ color: medal || "#64748b" }}>#{u.rank}</span>
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-white overflow-hidden ring-1 ring-white/10">
                      {u.picture ? <img src={u.picture} alt={u.name} loading="lazy" className="w-full h-full object-cover" /> : u.name?.[0]}
                    </div>
                    <span className="text-sm text-white flex-1 truncate">{u.name}</span>
                    <span className="text-xs font-bold text-[#FBBF24] tabular-nums">{u.xp} XP</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div variants={item}>
            <NavCard to="/resume-builder" testId="dashboard-resume-checker-card-sidebar" icon={FileSearch} color="#38bdf8" title="AI Resume Builder & Score Checker" desc="23 templates, AI prompt-fill, ATS score & JD tailoring" badge="New" />
          </motion.div>

          <motion.div variants={item} className="neu-card rounded-2xl p-6" data-testid="dashboard-recent-achievements">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2 mb-4"><Trophy className="w-3.5 h-3.5 text-[#FBBF24]" /> Recent Achievements</span>
            {data.recent_achievements?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_achievements.map((a) => {
                  const Icon = ACH_ICONS[a.icon] || Award;
                  return (
                    <motion.div key={a.id} className="flex items-center gap-3" data-testid={`dashboard-achievement-${a.id}`} whileHover={{ x: 3, scale: 1.02 }} transition={{ duration: 0.2 }}>
                      <div className="w-8 h-8 rounded-lg bg-[#FBBF24]/10 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#FBBF24]" />
                      </div>
                      <span className="text-sm text-white truncate">{a.title}</span>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-400">Complete your first challenge to start earning badges!</p>
            )}
          </motion.div>

          <motion.div variants={item} className="neu-card rounded-2xl p-6" data-testid="dashboard-upcoming-challenge">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2"><Award className="w-3.5 h-3.5" /> Upcoming</span>
            {data.upcoming_challenge ? (
              <>
                <h3 className="text-sm font-medium text-white mt-2">Day {data.upcoming_challenge.day}: {data.upcoming_challenge.title}</h3>
                <span className={`inline-block mt-2 px-2 py-0.5 rounded-full border text-[11px] font-medium ${DIFFICULTY_STYLE[data.upcoming_challenge.difficulty] || "bg-white/5 border-white/10 text-slate-300"}`}>{data.upcoming_challenge.difficulty}</span>
              </>
            ) : (
              <p className="text-xs text-slate-400 mt-2">You've unlocked every day of the challenge. Legendary!</p>
            )}
          </motion.div>
        </div>
      </div>

      {/* Explore More Tools — full-width Bento grid (previously a cramped single-column
          sidebar list; also fixes Courses/Creator Studio silently not rendering since they
          used to be nested inside another <NavCard> which never rendered its children). */}
      <motion.div variants={item} className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
            <Compass className="w-3.5 h-3.5 text-[#5C9DFF]" /> Explore More Tools
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dashboard-tools-grid">
          {TOOL_TILES.map((t) => <ToolTile key={t.to} {...t} />)}
        </div>
      </motion.div>
      </motion.div>

      <button
        onClick={() => setHelpOpen(true)}
        data-testid="dashboard-help-float-button"
        title="Need help?"
        className="fixed bottom-24 left-6 z-40 w-12 h-12 rounded-full bg-white/10 border border-white/15 backdrop-blur-md shadow-lg flex items-center justify-center hover:bg-white/15 hover:scale-110 transition-all"
      >
        <LifeBuoy className="w-5 h-5 text-indigo-300" />
      </button>
      <HelpSupportModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}
